/** Compound, stylised-realistic meshes for each part type.
 *  Each function returns a parented TransformNode positioned so that:
 *    - y=0 at the group origin is the part's bottom attach plane
 *    - The part extends upward by def.height
 *  (For rendering we re-center via group.position.y = p.y + def.height/2 — same as before.) */

import { Color3, Vector3 } from "@babylonjs/core/Maths/math";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";

import type { PartDef } from "./types";

/** Build a compound mesh for the given part, centered at y=0 (bottom at y=-h/2, top at y=+h/2). */
export function buildPartMesh(scene: Scene, def: PartDef, tag: string): TransformNode {
  const group = new TransformNode(`pg_${tag}`, scene);
  switch (def.id) {
    case "pod-mk1": return buildCapsule(scene, def, group, tag);
    case "probe-hex": return buildProbe(scene, def, group, tag);
    case "tank-lfo-s":
    case "tank-lfo-m":
    case "tank-lfo-l": return buildLiquidTank(scene, def, group, tag);
    case "engine-lfo": return buildLiquidEngine(scene, def, group, tag, "atlas");
    case "engine-lfo-big": return buildLiquidEngine(scene, def, group, tag, "hydra");
    case "booster-srb": return buildSRB(scene, def, group, tag);
    case "parachute": return buildParachute(scene, def, group, tag);
    case "fairing": return buildFairing(scene, def, group, tag);
    case "fins": return buildFins(scene, def, group, tag);
    case "decoupler": return buildDecoupler(scene, def, group, tag);
    case "legs": return buildLegs(scene, def, group, tag);
    case "solar": return buildSolar(scene, def, group, tag);
    default: return buildFallback(scene, def, group, tag);
  }
}

// ---- Materials ----
function mat(scene: Scene, name: string, hex: string, options?: { metallic?: boolean; emissive?: number }) {
  const m = new StandardMaterial(name, scene);
  m.diffuseColor = Color3.FromHexString(hex);
  m.specularColor = options?.metallic ? new Color3(0.35, 0.35, 0.38) : new Color3(0.08, 0.08, 0.08);
  m.specularPower = options?.metallic ? 64 : 32;
  if (options?.emissive) m.emissiveColor = Color3.FromHexString(hex).scale(options.emissive);
  return m;
}

// ---- Builders ----
function buildCapsule(scene: Scene, def: PartDef, g: TransformNode, tag: string) {
  const h = def.height;
  const r = def.halfWidth;

  // Conical hull (wider at bottom, narrow at top)
  const body = MeshBuilder.CreateCylinder(`cap_${tag}`, {
    diameterTop: r * 1.2,
    diameterBottom: r * 2,
    height: h * 0.78,
    tessellation: 28,
  }, scene);
  body.material = mat(scene, `cap_m_${tag}`, "#c5cdd9", { metallic: true });
  body.position.y = -h / 2 + (h * 0.78) / 2;
  body.parent = g;

  // Heat shield disc at base
  const shield = MeshBuilder.CreateCylinder(`cap_s_${tag}`, {
    diameterTop: r * 2,
    diameterBottom: r * 2,
    height: 0.08,
    tessellation: 28,
  }, scene);
  shield.material = mat(scene, `cap_sm_${tag}`, "#2b2b30");
  shield.position.y = -h / 2 + 0.04;
  shield.parent = g;

  // Rounded top cap
  const top = MeshBuilder.CreateSphere(`cap_t_${tag}`, {
    diameter: r * 1.2,
    segments: 16,
  }, scene);
  top.material = body.material;
  top.scaling.y = 0.6;
  top.position.y = -h / 2 + h * 0.78 + r * 0.3;
  top.parent = g;

  // Window (dark dome on the side)
  const win = MeshBuilder.CreateSphere(`cap_w_${tag}`, { diameter: r * 0.55, segments: 12 }, scene);
  win.material = mat(scene, `cap_wm_${tag}`, "#0a1830", { emissive: 0.2 });
  win.position.set(r * 0.88, -h / 2 + h * 0.55, 0);
  win.scaling.set(0.5, 0.5, 0.3);
  win.parent = g;

  // Parachute bay ring (at top)
  const ring = MeshBuilder.CreateTorus(`cap_r_${tag}`, { diameter: r * 1.2, thickness: 0.05, tessellation: 20 }, scene);
  ring.material = mat(scene, `cap_rm_${tag}`, "#3a4253", { metallic: true });
  ring.position.y = -h / 2 + h * 0.78 + r * 0.55;
  ring.parent = g;
  return g;
}

function buildProbe(scene: Scene, def: PartDef, g: TransformNode, tag: string) {
  const r = def.halfWidth;
  const h = def.height;
  const body = MeshBuilder.CreateCylinder(`pr_${tag}`, {
    diameter: r * 2,
    height: h,
    tessellation: 6,
  }, scene);
  body.material = mat(scene, `pr_m_${tag}`, "#7a8494", { metallic: true });
  body.parent = g;

  // Antenna nub
  const ant = MeshBuilder.CreateCylinder(`pr_a_${tag}`, { diameter: 0.06, height: 0.3, tessellation: 8 }, scene);
  ant.material = mat(scene, `pr_am_${tag}`, "#1a2030", { metallic: true });
  ant.position.y = h / 2 + 0.15;
  ant.parent = g;
  return g;
}

function buildLiquidTank(scene: Scene, def: PartDef, g: TransformNode, tag: string) {
  const r = def.halfWidth;
  const h = def.height;
  const body = MeshBuilder.CreateCylinder(`t_${tag}`, {
    diameter: r * 2,
    height: h,
    tessellation: 28,
  }, scene);
  body.material = mat(scene, `t_m_${tag}`, "#eceff3", { metallic: true });
  body.parent = g;

  // End caps (slightly darker)
  const capTop = MeshBuilder.CreateTorus(`t_ct_${tag}`, { diameter: r * 2, thickness: 0.04, tessellation: 24 }, scene);
  capTop.material = mat(scene, `t_cm_${tag}`, "#9097a5", { metallic: true });
  capTop.position.y = h / 2 - 0.02;
  capTop.parent = g;
  const capBot = capTop.clone(`t_cb_${tag}`);
  if (capBot) capBot.position.y = -h / 2 + 0.02;

  // Stripe bands (2 bands)
  for (let i = 0; i < 2; i++) {
    const band = MeshBuilder.CreateCylinder(`t_b${i}_${tag}`, {
      diameter: r * 2 + 0.01,
      height: 0.12,
      tessellation: 28,
    }, scene);
    band.material = mat(scene, `t_bm${i}_${tag}`, i === 0 ? "#ff6a3d" : "#2c3240");
    band.position.y = -h / 2 + (h / 3) * (i + 1);
    band.parent = g;
  }

  return g;
}

function buildLiquidEngine(scene: Scene, def: PartDef, g: TransformNode, tag: string, variant: "atlas" | "hydra") {
  const r = def.halfWidth;
  const h = def.height;

  // Injector cylinder at top
  const block = MeshBuilder.CreateCylinder(`en_b_${tag}`, {
    diameterTop: r * 1.7,
    diameterBottom: r * 1.6,
    height: h * 0.22,
    tessellation: 24,
  }, scene);
  block.material = mat(scene, `en_bm_${tag}`, "#4a525f", { metallic: true });
  block.position.y = h / 2 - (h * 0.22) / 2;
  block.parent = g;

  // Bell (flared nozzle)
  const bell = MeshBuilder.CreateCylinder(`en_n_${tag}`, {
    diameterTop: r * 1.2,
    diameterBottom: r * 2.0,
    height: h * 0.72,
    tessellation: 28,
  }, scene);
  bell.material = mat(scene, `en_nm_${tag}`, variant === "hydra" ? "#2c303a" : "#7d848f", { metallic: true });
  bell.position.y = h / 2 - h * 0.22 - (h * 0.72) / 2;
  bell.parent = g;

  // Inner throat (darker)
  const throat = MeshBuilder.CreateCylinder(`en_t_${tag}`, {
    diameterTop: r * 0.8,
    diameterBottom: r * 0.8,
    height: h * 0.05,
    tessellation: 20,
  }, scene);
  throat.material = mat(scene, `en_tm_${tag}`, "#0c0c10");
  throat.position.y = h / 2 - h * 0.22 - 0.02;
  throat.parent = g;

  // Turbopump plumbing (small tubes on the side)
  for (let i = 0; i < 3; i++) {
    const ang = (i * Math.PI * 2) / 3;
    const tube = MeshBuilder.CreateCylinder(`en_p${i}_${tag}`, { diameter: 0.06, height: h * 0.5, tessellation: 8 }, scene);
    tube.material = mat(scene, `en_pm_${tag}`, "#9098a8", { metallic: true });
    tube.position.set(Math.cos(ang) * r * 0.9, h / 2 - h * 0.35, Math.sin(ang) * r * 0.9);
    tube.parent = g;
  }

  return g;
}

function buildSRB(scene: Scene, def: PartDef, g: TransformNode, tag: string) {
  const r = def.halfWidth;
  const h = def.height;
  // Body
  const body = MeshBuilder.CreateCylinder(`srb_${tag}`, { diameter: r * 2, height: h, tessellation: 28 }, scene);
  body.material = mat(scene, `srb_m_${tag}`, "#d9b87b", { metallic: false });
  body.parent = g;

  // Nose cone
  const nose = MeshBuilder.CreateCylinder(`srb_n_${tag}`, {
    diameterTop: 0.02,
    diameterBottom: r * 2,
    height: r * 1.4,
    tessellation: 28,
  }, scene);
  nose.material = mat(scene, `srb_nm_${tag}`, "#c79961");
  nose.position.y = h / 2 + r * 0.7;
  nose.parent = g;

  // Tail nozzle
  const noz = MeshBuilder.CreateCylinder(`srb_nz_${tag}`, {
    diameterTop: r * 1.2,
    diameterBottom: r * 1.6,
    height: r * 0.9,
    tessellation: 20,
  }, scene);
  noz.material = mat(scene, `srb_nzm_${tag}`, "#2c303a");
  noz.position.y = -h / 2 - r * 0.45;
  noz.parent = g;

  // Bands
  for (let i = 0; i < 3; i++) {
    const band = MeshBuilder.CreateCylinder(`srb_b${i}_${tag}`, {
      diameter: r * 2 + 0.02,
      height: 0.1,
      tessellation: 28,
    }, scene);
    band.material = mat(scene, `srb_bm${i}_${tag}`, "#7a5c32");
    band.position.y = -h / 2 + (h / 4) * (i + 1);
    band.parent = g;
  }

  return g;
}

function buildParachute(scene: Scene, def: PartDef, g: TransformNode, tag: string) {
  const r = def.halfWidth;
  const h = def.height;
  // Housing
  const housing = MeshBuilder.CreateCylinder(`pa_${tag}`, { diameter: r * 2, height: h, tessellation: 20 }, scene);
  housing.material = mat(scene, `pa_m_${tag}`, "#4a5463", { metallic: true });
  housing.parent = g;

  // Stored canopy (visible dome above)
  const cap = MeshBuilder.CreateSphere(`pa_c_${tag}`, { diameter: r * 1.6, segments: 16 }, scene);
  cap.material = mat(scene, `pa_cm_${tag}`, "#d94f40");
  cap.scaling.y = 0.45;
  cap.position.y = h / 2 + 0.02;
  cap.parent = g;
  return g;
}

function buildFairing(scene: Scene, def: PartDef, g: TransformNode, tag: string) {
  const r = def.halfWidth;
  const h = def.height;
  // Two half-cones (left and right) for the iconic clamshell look
  const left = MeshBuilder.CreateCylinder(`fr_L_${tag}`, {
    diameterTop: r * 0.4,
    diameterBottom: r * 2,
    height: h,
    tessellation: 18,
    arc: 0.5,
  }, scene);
  left.material = mat(scene, `fr_m_${tag}`, "#f2f4f8", { metallic: true });
  left.parent = g;

  // Seam stripe running vertically
  const seam = MeshBuilder.CreateBox(`fr_s_${tag}`, { width: 0.04, height: h, depth: r * 0.08 }, scene);
  seam.material = mat(scene, `fr_sm_${tag}`, "#8a909c", { metallic: true });
  seam.position.x = 0;
  seam.position.z = r * 1.0;
  seam.parent = g;

  return g;
}

function buildFins(scene: Scene, def: PartDef, g: TransformNode, tag: string) {
  // Two fins mirrored on ±Z
  const r = def.halfWidth;
  const h = def.height;
  const make = (side: number, name: string): Mesh => {
    const fin = MeshBuilder.CreateBox(name, { width: r * 1.8, height: h, depth: 0.04 }, scene);
    fin.material = mat(scene, `${name}_m`, "#39414f", { metallic: true });
    fin.position.z = side * r * 0.1;
    // Swept leading edge: rotate slightly
    fin.rotation.y = side * 0.1;
    fin.parent = g;
    return fin;
  };
  make(1, `fn_r_${tag}`);
  make(-1, `fn_l_${tag}`);
  return g;
}

function buildDecoupler(scene: Scene, def: PartDef, g: TransformNode, tag: string) {
  const r = def.halfWidth;
  const h = def.height;
  const body = MeshBuilder.CreateCylinder(`dc_${tag}`, { diameter: r * 2, height: h, tessellation: 28 }, scene);
  body.material = mat(scene, `dc_m_${tag}`, "#565e6b", { metallic: true });
  body.parent = g;

  // Bolt ring
  for (let i = 0; i < 8; i++) {
    const ang = (i * Math.PI * 2) / 8;
    const bolt = MeshBuilder.CreateBox(`dc_b${i}_${tag}`, { width: 0.06, height: 0.06, depth: 0.06 }, scene);
    bolt.material = mat(scene, `dc_bm_${tag}`, "#2b2f38");
    bolt.position.set(Math.cos(ang) * r * 0.92, 0, Math.sin(ang) * r * 0.92);
    bolt.parent = g;
  }
  return g;
}

function buildLegs(scene: Scene, def: PartDef, g: TransformNode, tag: string) {
  const h = def.height;
  // Strut + foot pad
  const strut = MeshBuilder.CreateBox(`lg_s_${tag}`, { width: 0.08, height: h, depth: 0.08 }, scene);
  strut.material = mat(scene, `lg_sm_${tag}`, "#6c7583", { metallic: true });
  strut.parent = g;

  const pad = MeshBuilder.CreateCylinder(`lg_p_${tag}`, { diameter: 0.35, height: 0.05, tessellation: 16 }, scene);
  pad.material = mat(scene, `lg_pm_${tag}`, "#3a4050", { metallic: true });
  pad.position.y = -h / 2;
  pad.parent = g;

  // Diagonal brace
  const brace = MeshBuilder.CreateBox(`lg_b_${tag}`, { width: 0.04, height: h * 0.7, depth: 0.04 }, scene);
  brace.material = strut.material;
  brace.rotation.z = 0.5;
  brace.position.x = 0.12;
  brace.parent = g;
  return g;
}

function buildSolar(scene: Scene, def: PartDef, g: TransformNode, tag: string) {
  const r = def.halfWidth;
  // Frame
  const frame = MeshBuilder.CreateBox(`sp_f_${tag}`, { width: r * 2, height: 0.03, depth: r * 1.3 }, scene);
  frame.material = mat(scene, `sp_fm_${tag}`, "#3a4050", { metallic: true });
  frame.parent = g;

  // Panel face (emissive blue)
  const face = MeshBuilder.CreateBox(`sp_p_${tag}`, { width: r * 1.9, height: 0.008, depth: r * 1.2 }, scene);
  const m = new StandardMaterial(`sp_pm_${tag}`, scene);
  m.diffuseColor = Color3.FromHexString("#1f3a7a");
  m.emissiveColor = new Color3(0.08, 0.12, 0.3);
  m.specularColor = new Color3(0.5, 0.5, 0.6);
  face.material = m;
  face.position.y = 0.02;
  face.parent = g;

  // Cell grid (stripes)
  for (let i = 1; i < 4; i++) {
    const stripe = MeshBuilder.CreateBox(`sp_c${i}_${tag}`, { width: r * 1.9, height: 0.01, depth: 0.01 }, scene);
    stripe.material = mat(scene, `sp_cm${i}_${tag}`, "#0a1530");
    stripe.position.set(0, 0.025, -r * 0.5 + (i * r) / 2);
    stripe.parent = g;
  }

  return g;
}

function buildFallback(scene: Scene, def: PartDef, g: TransformNode, tag: string) {
  const b = MeshBuilder.CreateBox(`fb_${tag}`, { width: def.halfWidth * 2, height: def.height, depth: def.halfWidth * 2 }, scene);
  b.material = mat(scene, `fb_m_${tag}`, def.render.color);
  b.parent = g;
  return g;
}
